import React, { useState, useEffect } from "react";
import { Box, Button, TextField, Typography, Modal, IconButton, Card, CardContent, Divider, Alert } from "@mui/material";
import { Edit as EditIcon, Close as CloseIcon } from "@mui/icons-material";
import SearchPatient from "./SearchPatient";
import { DateField } from "@mui/x-date-pickers/DateField";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { MuiTelInput } from "mui-tel-input";
import { SERVER_BASE_URL } from "../Config";
import dayjs, { Dayjs } from "dayjs";
import PatientAddressManager from "./PatientAddressManager";
import { joinPatientName, splitPatientName } from "./patientName";

// Define the props type
interface PatientFormProps {
  updatePatientId: (id: number | null) => void; // Specify the type of function expected
  onPatientSaved?: (patient: any) => void; // For batching mode
  editingPatient?: any; // For editing existing patient in batching mode
  modalOnly?: boolean; // When true, only show the modal (hide inline UI)
  onAddressesChange?: (patientId: number) => void; // Callback when addresses are modified
  initialPatient?: any; // Pre-selected patient (e.g. from reorder)
}

const PatientForm: React.FC<PatientFormProps> = ({ updatePatientId, onPatientSaved, editingPatient, modalOnly = false, onAddressesChange, initialPatient }) => {
  const [showPatientModal, setShowPatientModal] = useState(false);
  const [patientTelephone, setPatientTelephone] = useState("");
  const [patientId, setLocalPatientId] = useState<number | null>(null);
  const [patientLastName, setPatientLastName] = useState("");
  const [patientFirstName, setPatientFirstName] = useState("");
  const [patientDob, setPatientDob] = useState<Dayjs | null>(null);
  const [patientEmail, setPatientEmail] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<any>(initialPatient || null);
  const [errorMessage, setErrorMessage] = useState("");

  // Load editing patient data when editingPatient prop is provided
  useEffect(() => {
    if (editingPatient) {
      const { firstName, lastName } = splitPatientName(editingPatient.name);
      setPatientId(editingPatient.id);
      setPatientFirstName(firstName);
      setPatientLastName(lastName);
      setPatientDob(editingPatient.dob ? dayjs(editingPatient.dob) : null);
      setPatientEmail(editingPatient.email || "");
      setPatientTelephone(editingPatient.phone || "");
      setIsEditing(true);
      setSelectedPatient(editingPatient);
      setShowPatientModal(true);
    } else if (modalOnly) {
      // In modalOnly mode, if editingPatient becomes null, close the modal
      setShowPatientModal(false);
    }
  }, [editingPatient, modalOnly]);

  const setPatientId = (patientId: any) => {
    // console.log(patientId)
    // updatePatientId(patientId);
    setLocalPatientId(patientId);
  };

  const handleNewPatientClick = () => {
    // Reset form for new patient
    setPatientDob(null);
    setPatientTelephone("");
    setPatientId(null); // Ensure patientId is null to indicate a new patient
    setPatientLastName("");
    setPatientFirstName("");
    setPatientEmail("");
    setIsEditing(false);
    setShowPatientModal(true);
  };

  const handleExistingPatientSelect = (patient: any) => {
    // Set selected patient without opening the edit modal
    updatePatientId(patient.id);
    setSelectedPatient(patient);
    // Call onPatientSaved callback if provided (for non-batch mode confirmation)
    if (onPatientSaved) {
      onPatientSaved(patient);
    }
  };

  const handleEditPatientClick = () => {
    if (selectedPatient) {
      // Load patient data into form for editing
      const { firstName, lastName } = splitPatientName(selectedPatient.name);
      setPatientId(selectedPatient.id);
      setPatientFirstName(firstName);
      setPatientLastName(lastName);
      if (selectedPatient.dob) {
        setPatientDob(dayjs(selectedPatient.dob));
      }
      setPatientEmail(selectedPatient.email);
      setPatientTelephone(selectedPatient.phone);
      setIsEditing(true);
      setShowPatientModal(true);
    }
  };

  const handleCloseModal = () => {
    setShowPatientModal(false);
    setErrorMessage(""); // Clear error message when closing modal
  };

  const handleCreateOrUpdatePatient = async (closeOnCreate: boolean = false, fromAddressManager: boolean = false) => {
    // Clear any previous error messages
    setErrorMessage("");

    // Validation for required fields
    if (!patientLastName.trim()) {
      setErrorMessage("Patient last name is required");
      return false;
    }

    if (!patientFirstName.trim()) {
      setErrorMessage("Patient first name is required");
      return false;
    }

    if (!patientTelephone.trim()) {
      setErrorMessage("Patient phone number is required");
      return false;
    }

    if (!patientDob) {
      setErrorMessage("Patient date of birth is required");
      return false;
    }

    const providerToken = localStorage.getItem("providerToken");
    let url = `${SERVER_BASE_URL}/patient`;
    let method = "POST";

    let editMode = false;
    if (isEditing && patientId) {
      editMode = true;
      url = `${SERVER_BASE_URL}/patient/${patientId}`;
      method = "PUT";
    } else {
      console.log("creating patient");
    }

    const fullName = joinPatientName(patientFirstName, patientLastName);
    const body = {
      firstName: patientFirstName.trim(),
      lastName: patientLastName.trim(),
      name: fullName, // Server rebuilds this from the parts; sent for compatibility
      dob: patientDob ? patientDob.format("YYYY-MM-DD") : null,
      email: patientEmail,
      phone: patientTelephone
    };

    try {
      const response = await fetch(url, {
        method: method,
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${providerToken}`
        },
        body: JSON.stringify(body)
      });

      const data = await response.json();
      setPatientId(data.id);

      console.log("Patient Created or Updated");

      // Automatically select the new patient after creation or update
      const newPatient = {
        id: data.id,
        name: fullName,
        dob: patientDob ? patientDob.format("YYYY-MM-DD") : null,
        email: patientEmail,
        phone: patientTelephone
      };

      // Always set editing mode after patient is created/updated
      setIsEditing(true);
      setSelectedPatient(newPatient);

      // Only update parent when not called from address manager to prevent modal from closing
      if (!fromAddressManager) {
        updatePatientId(newPatient.id);
        // Call onPatientSaved if provided (for batching mode)
        if (onPatientSaved) {
          onPatientSaved(newPatient);
        }
      }

      if (editMode) {
        handleCloseModal();
      }
      return data.id;
    } catch (error) {
      console.error("Error creating or updating patient:", error);
      setErrorMessage("Failed to create or update patient. Please try again.");
      return false;
    }
  };

  const handleCreateOrUpdatePatientWrapper = async () => {
    const success = await handleCreateOrUpdatePatient(true, false);
    if (success !== false) {
      setShowPatientModal(false);
    }
  };

  const handleCreatePatientForAddress = async () => {
    const success = await handleCreateOrUpdatePatient(false, true);
    return success;
  };

  return (
    <Box>

      {!modalOnly && (
        <>
          <SearchPatient onSelect={handleExistingPatientSelect} />

          <Button
            variant="contained"
            color="primary"
            onClick={handleNewPatientClick}
            sx={{ width: "100%", mt: 1 }}
          >
            + New Patient
          </Button>

          {selectedPatient && (
            <Card sx={{ mt: 2, p: 2 }}>
              <CardContent sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="body1">
                  Selected Patient: {selectedPatient.name}, DOB: {selectedPatient.dob}
                </Typography>
                <IconButton onClick={handleEditPatientClick} color="primary">
                  <EditIcon />
                </IconButton>
              </CardContent>
            </Card>
          )}
        </>
      )}

      <Modal
        open={showPatientModal}
        onClose={handleCloseModal}
        aria-labelledby="patient-modal"
        aria-describedby="modal-to-create-or-edit-patient"
        sx={{ zIndex: 1400 }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "92%", sm: 400 },
            maxHeight: "90vh",
            overflowY: "auto",
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: { xs: 2, sm: 4 }
          }}
        >
          <IconButton
            onClick={handleCloseModal}
            sx={{
              position: "absolute",
              top: 8,
              right: 8
            }}
          >
            <CloseIcon />
          </IconButton>
          <Typography variant="h6" component="h2" gutterBottom>
            {isEditing ? "Edit Patient" : "Create New Patient"}
          </Typography>
          
          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
          )}
          <TextField
            label="Last Name"
            variant="outlined"
            required
            fullWidth
            sx={{ mb: 2 }}
            value={patientLastName}
            onChange={(e) => {
              setPatientLastName(e.target.value);
              if (errorMessage) setErrorMessage("");
            }}
          />
          <TextField
            label="First Name"
            variant="outlined"
            required
            fullWidth
            sx={{ mb: 2 }}
            value={patientFirstName}
            onChange={(e) => {
              setPatientFirstName(e.target.value);
              if (errorMessage) setErrorMessage("");
            }}
          />
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateField
              label="Date of Birth"
              fullWidth
              sx={{ mb: 2 }}
              value={patientDob}
              onChange={(newValue) => {
                setPatientDob(newValue);
                if (errorMessage) setErrorMessage("");
              }}
            />
          </LocalizationProvider>
          <TextField
            label="Email (Optional)"
            variant="outlined"
            fullWidth
            sx={{ mb: 2 }}
            value={patientEmail}
            onChange={(e) => {
              setPatientEmail(e.target.value);
              if (errorMessage) setErrorMessage("");
            }}
          />
          <MuiTelInput
            label="Phone"
            fullWidth
            value={patientTelephone}
            onChange={(value) => {
              if (value !== patientTelephone) {
                setPatientTelephone(value);
                if (errorMessage) setErrorMessage("");
              }
            }}
            defaultCountry={"US"}
            disableDropdown
          />
          <p></p>
          {/*<Button variant="contained" color="primary" onClick={handleCreateOrUpdatePatientWrapper}>*/}
          {/*  {isEditing ? "Update Patient" : "Create Patient"}*/}
          {/*</Button>*/}

          {/* Show PatientAddressManager when creating or editing a patient */}
          <PatientAddressManager patientId={patientId || null} setPatientId={setPatientId}
                                 createPatientFn={handleCreatePatientForAddress}
                                 onAddressesChange={onAddressesChange} />

          <br />
          <Divider></Divider>
          <br />
          <Button variant="contained" color="primary" onClick={handleCreateOrUpdatePatientWrapper}>
            {isEditing ? "Update Patient" : "Create Patient"}
          </Button>
        </Box>
      </Modal>
    </Box>
  );
};

export default PatientForm;
